import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { General } from 'icons/untitledui';

import * as Text from 'theme/components/Text.style';
import * as Section from 'settings/styles/SettingsSection.style';
import * as Styled from 'settings/styles/ShiftSchedule.style';
import TimeSelector from 'settings/components/TimeSelector';

export type DayKey =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface ShiftScheduleSettings {
  autoSchedule: boolean;
  time: string;
  days: DayKey[];
}

interface ShiftScheduleProps {
  value: ShiftScheduleSettings;
  onChange: (next: ShiftScheduleSettings) => void | Promise<void>;
}

const DAYS: DayKey[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

export default function ShiftSchedule({
  value,
  onChange,
}: ShiftScheduleProps): JSX.Element {
  const { t } = useTranslation('pageSettings');
  const daysGridRef = useRef<HTMLDivElement | null>(null);
  const [useTabletDayButtons, setUseTabletDayButtons] = useState(false);

  useEffect(() => {
    const el = daysGridRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return () => {};

    let raf = 0;
    const measure = () => {
      const { width } = el.getBoundingClientRect();
      const computed = window.getComputedStyle(el);
      // `gap` can be defined as `gap`, `column-gap`, or both depending on layout.
      const gapPx = parseFloat(computed.columnGap || computed.gap || '0') || 0;

      // If we tried to fit all 7 days in one row, what would each button get?
      // If it drops below 101px, switch to tablet styling (vertical list).
      const perButtonWidth = (width - gapPx * 6) / 7;
      const shouldUseTablet = perButtonWidth < 101;

      setUseTabletDayButtons((prev) =>
        prev === shouldUseTablet ? prev : shouldUseTablet
      );
    };

    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });

    observer.observe(el);
    measure();

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, []);

  const toggleDay = (day: DayKey) => {
    const isSelected = value.days.includes(day);
    const nextDays = isSelected
      ? value.days.filter((d) => d !== day)
      : [...value.days, day];
    onChange({ ...value, days: nextDays });
  };

  return (
    <>
      <Section.RowListItem $mobileColumn>
        <Section.RowLabel>
          <Text.P2Semibold>{t('shiftSchedule.reminderTime')}</Text.P2Semibold>
        </Section.RowLabel>
        <Section.RowControl>
          <TimeSelector
            ariaLabel={t('shiftSchedule.reminderTime')}
            value={value.time}
            onChange={(nextHHMM) => onChange({ ...value, time: nextHHMM })}
          />
        </Section.RowControl>
      </Section.RowListItem>

      <Section.Divider />

      <Section.ColumnRowListItem>
        <Section.RowLabelTopPadding>
          <Text.P2Semibold>{t('shiftSchedule.repeatOn')}</Text.P2Semibold>
        </Section.RowLabelTopPadding>
        <Styled.DaysGrid ref={daysGridRef} $tabletLike={useTabletDayButtons}>
          {DAYS.map((day) => {
            const selected = value.days.includes(day);
            return (
              <Styled.DayButton
                key={day}
                $selected={selected}
                $tabletLike={useTabletDayButtons}
                onClick={() => toggleDay(day)}
                aria-pressed={selected}
                aria-label={t(`days.${day}`)}
                type="button"
              >
                <Text.P2 style={{ color: 'inherit' }}>
                  {t(`days.${day}`)}
                </Text.P2>
                <Styled.DayCheck $selected={selected}>
                  {selected ? <General.Check /> : null}
                </Styled.DayCheck>
              </Styled.DayButton>
            );
          })}
        </Styled.DaysGrid>
      </Section.ColumnRowListItem>
    </>
  );
}
